/**
 * Delivery picker — postcode lookup, delivery option and delivery date, driven
 * as steps 1-3 of the cart drawer flow (step 0 is the cart itself).
 *
 * Ported from Aurelo's lightning-cart.js and rewritten without jQuery. The
 * delivery service contract is unchanged: same /api/check-postcode,
 * /api/shipping and /api/carrier-sync calls, same x-store header, same
 * Delivery-Option and _delivery-date cart attributes and line item properties.
 *
 * Differences from Aurelo worth knowing about:
 *
 *  - Aurelo wrote the line item properties to the last line only. Every line
 *    gets them here, and the properties already on each line are re-read from
 *    /cart.js at write time rather than taken from a snapshot — see
 *    writeCartProperties.
 *  - Aurelo's cart writes ignored the response status, so a rejected change.js
 *    left the cart attributes set and the line item properties missing with
 *    nothing logged.
 *
 *  - The basket payload comes from the #delivery-picker-data JSON block rendered
 *    by snippets/delivery-picker.liquid, so it is rebuilt by Liquid on every
 *    cart drawer refresh. Aurelo kept a `g_basket` global and refreshed it from
 *    /cart.js, whose items use `quantity` instead of `qty` and carry no
 *    `restock`, so its payload broke after the first cart change.
 *  - Aurelo read `event.target.dataset.flow`, which is the inner
 *    <button-content> when the label is clicked, giving NaN. This uses
 *    currentTarget.
 *  - The "no delivery available" redirect was hard-coded to bridgman.co.uk. It
 *    is now the delivery_picker_no_delivery_url theme setting, and with that
 *    blank the picker just shows the no-delivery message.
 *  - Hotjar (`hj`) and Spurit are optional; both are feature-detected.
 */
(() => {
  const ONE_DAY_IN_MS = 24 * 60 * 60 * 1000;
  const MONTH_STRING_ARRAY = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  /** Per-render listener scope, so a rebuilt calendar doesn't stack handlers. */
  let calendarListeners = null;
  let checkoutSyncBound = false;
  /** Third-party line item properties to preserve on every write. */
  const carriedProperties = {};

  const config = () => {
    const el = document.getElementById("delivery-picker-data");
    if (!el) return null;

    try {
      return {
        el,
        apiBaseUrl: el.dataset.apiBaseUrl || "",
        shop: el.dataset.shop || (window.Shopify && window.Shopify.shop) || "",
        noDeliveryUrl: el.dataset.noDeliveryUrl || "",
        dateRequiredCode: el.dataset.dateRequiredCode || "",
        basket: JSON.parse(el.textContent),
      };
    } catch (error) {
      console.error("Delivery picker: could not parse basket data", error);
      return null;
    }
  };

  /**
   * Calls the delivery service. Mirrors Aurelo's SendAPIRequest, including the
   * x-store header and the (err, result) callback shape.
   */
  const sendApiRequest = (endpoint, method, params, callback) => {
    const settings = config();
    if (!settings) return;

    let url = settings.apiBaseUrl + endpoint;
    let body = null;

    if (method === "GET") {
      if (params) {
        const query = new URLSearchParams(params);
        url += (url.includes("?") ? "&" : "?") + query.toString();
      }
    } else if (params) {
      body = JSON.stringify(params);
    }

    fetch(url, {
      method,
      headers: { "Content-Type": "application/json", "x-store": settings.shop },
      body,
    })
      .then(async (response) => {
        let parsed;
        try {
          const text = await response.text();
          if (text) parsed = JSON.parse(text);
        } catch (error) {
          console.log(`Delivery picker: error parsing server response: ${error.message}`);
        }
        if (callback) callback(response.ok ? null : response.status, parsed);
      })
      .catch((error) => {
        console.error("Delivery picker: request failed", error);
        if (callback) callback(error, undefined);
      });
  };

  /* ---------------------------------------------------------------------------
     Cart persistence
     ------------------------------------------------------------------------ */

  /** Current lines, for their keys, quantities and existing properties. */
  const getCart = async () => {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) {
      console.error("Delivery picker: could not read the cart", error);
      return null;
    }
  };

  /**
   * A silent 4xx here is how the line item properties went missing while the
   * cart attributes kept working: fetch only rejects on network errors, so a
   * rejected change.js was indistinguishable from a successful one.
   */
  const postCartData = async (data, api) => {
    try {
      const response = await fetch(window.Shopify.routes.root + api, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        console.error(`Delivery picker: ${api} returned ${response.status}`, await response.text());
      }
      return response.ok;
    } catch (error) {
      console.error("Delivery picker: cart update failed", error);
      return false;
    }
  };

  /**
   * Writes each name/value as both a cart attribute and a line item property.
   * Both calls are needed: update.js applies `attributes` and ignores
   * `properties`, change.js does the reverse, and the delivery service reads the
   * attributes while the order needs the properties.
   *
   * Aurelo's lightning-cart.js wrote the properties to the last line item only;
   * every line gets them here.
   *
   * Two details this has been wrong about, both worth not repeating:
   *
   *  - The existing properties have to be re-read from /cart.js on every write,
   *    not taken from the Liquid basket payload. change.js replaces a line's
   *    properties outright, so a write that starts from the payload rendered
   *    with the drawer drops whatever an earlier write in the same session added
   *    — which is how Delivery-Option went missing while the cart attribute,
   *    which update.js merges, looked fine.
   *  - Lines are addressed by 1-based position. change.js rejects a line item
   *    key passed as `id` with "no valid id or line parameter".
   */
  const writeCartProperties = async (properties) => {
    const attributes = {};
    properties.forEach(({ name, value }) => {
      attributes[name] = value;
    });

    await postCartData({ attributes }, "cart/update.js");

    const cart = await getCart();
    const items = cart ? cart.items || [] : [];

    for (let index = 0; index < items.length; index += 1) {
      const merged = { ...(items[index].properties || {}), ...carriedProperties, ...attributes };
      await postCartData({ line: index + 1, quantity: items[index].quantity, properties: merged }, "cart/change.js");
    }
  };

  /** Serialised: each write reads the cart first, so two must not interleave. */
  let cartWrites = Promise.resolve();

  const setCartProperties = (properties) => {
    cartWrites = cartWrites
      .then(() => writeCartProperties(properties))
      .catch((error) => console.error("Delivery picker: cart write failed", error));
    return cartWrites;
  };

  /* ---------------------------------------------------------------------------
     Flow navigation
     ------------------------------------------------------------------------ */

  const steps = () => document.querySelectorAll(".delivery-flow__step");
  const nextButton = () => document.querySelector(".delivery-picker-button--next");
  const cartButton = () => document.querySelector(".delivery-picker-button--cart");
  const checkoutButton = () => document.querySelector('button[name="checkout"]');
  const postcodeInput = () => document.getElementById("delivery-picker-postcode");

  const showPostcodeError = (show) => {
    const error = document.querySelector(".delivery-picker__postcode-error");
    if (error) error.classList.toggle("hidden", !show);
  };

  const toggleCheckoutButton = (showCheckout) => {
    checkoutButton()?.classList.toggle("hidden", !showCheckout);
    nextButton()?.classList.toggle("hidden", showCheckout);
  };

  /** Dims step 3 in the indicator when the chosen option needs no date. */
  const toggleDateStepIndicator = (enabled) => {
    const step = document.querySelector('.delivery-flow__step[data-flow-index="2"] .delivery-picker-progress .flow-3');
    step?.classList.toggle("disabled", !enabled);
  };

  const autoSelectFirstOption = () => {
    document.querySelector('.delivery-options input[type="radio"]')?.click();
  };

  /**
   * True when the option currently chosen carries a delivery date, and so has a
   * date step left to reach. Nothing chosen yet counts as needing one, so the
   * flow always offers a way forward.
   */
  const selectedOptionNeedsDate = () => {
    const settings = config();
    const selected = document.querySelector('input[name="attributes[Delivery-Option]"]:checked');
    if (!selected) return true;
    return !!settings && selected.value === settings.dateRequiredCode;
  };

  const goToStep = (index) => {
    const allSteps = steps();
    const next = nextButton();

    allSteps.forEach((step) => step.classList.add("hidden"));
    document.querySelector(`.delivery-flow__step[data-flow-index="${index}"]`)?.classList.remove("hidden");

    if (next) next.setAttribute("data-flow", index + 1);

    switch (index) {
      case 0:
        cartButton()?.classList.add("hidden");
        if (next) next.textContent = window.theme?.strings?.deliveryPicker?.toDelivery || "To Delivery";
        toggleCheckoutButton(false);
        break;
      case 1:
        if (next) next.textContent = window.theme?.strings?.deliveryPicker?.selectOption || "Select Option";
        cartButton()?.classList.remove("hidden");
        // The postcode step always advances. HandleDeliveryOptionClick hides the
        // advance button whenever the chosen option needs no date, so without
        // this a step change can land here with no way forward.
        toggleCheckoutButton(false);
        break;
      case 2:
        if (next) next.textContent = window.theme?.strings?.deliveryPicker?.selectDate || "Select Date";
        toggleCheckoutButton(!selectedOptionNeedsDate());
        break;
      default:
        break;
    }
  };

  const clearDatePicker = () => {
    const week = document.querySelector(".date-selector-week");
    if (week) week.replaceChildren();
    calendarListeners?.abort();
    calendarListeners = null;
  };

  const checkForValidPostcode = (callback) => {
    const input = postcodeInput();
    const settings = config();

    if (!input || !input.value) {
      callback();
      return;
    }

    sendApiRequest("/api/check-postcode", "POST", { store: settings ? settings.shop : "", postcode: input.value }, (err, result) => {
      if (err || (result && result.msg === "postcode found")) {
        callback();
      } else if (result && result.msg === "no postcode found") {
        document.getElementById("delivery-picker-date-controls")?.classList.add("hidden");
      }
    });
  };

  /**
   * Advances (or returns to) a step. Called from the inline onclick on the flow
   * buttons, so it has to be global.
   */
  window.cartFlowNext = (event) => {
    // Optional Spurit Split Partial Payments integration: carry the installment
    // property over so it isn't lost when we write to the line items. Its value
    // may only exist in the DOM at this point, so it can't be picked up from the
    // cart the way the other existing properties are.
    if (typeof Spurit !== "undefined" && Spurit.SplitPartialPayments2) {
      const propName = Spurit.SplitPartialPayments2.settings.config.installment.cart_line_item;
      const installmentInput = document.querySelector(`input[name="properties[${propName}]"]`);
      if (installmentInput && installmentInput.value) {
        carriedProperties[propName] = installmentInput.value;
      }
    }

    const target = Number(event.currentTarget.dataset.flow);
    if (Number.isNaN(target)) return;

    if (target === 2) {
      // Leaving the postcode step: a postcode is required.
      const input = postcodeInput();
      if (input && input.value) {
        showPostcodeError(false);
        checkForValidPostcode(() => {
          // Recorded here rather than on the way to the date step: an option
          // that needs no date never reaches that step, and its order still
          // has to carry the postcode.
          setCartProperties([{ name: "_postcode", value: input.value }]);
          autoSelectFirstOption();
          goToStep(target);
        });
      } else {
        showPostcodeError(true);
      }
    } else if (target === 3) {
      // Leaving the option step: fetch the dates before showing the calendar.
      getAvailableDates(() => goToStep(target));
    } else if (target !== 4) {
      clearDatePicker();
      goToStep(target);
    }
  };

  /**
   * Records the chosen delivery option. Called from the inline onclick on each
   * radio, so it has to be global.
   */
  window.HandleDeliveryOptionClick = (input) => {
    const settings = config();
    const dateRequiredCode = settings ? settings.dateRequiredCode : "";

    // The highlight goes on the option box, not the label that draws the radio.
    // main.css declares .color-scheme--4 after --2 at the same specificity, so
    // adding it to a box that carries --2 recolours it; removing it reverts.
    document.querySelectorAll(".delivery-option").forEach((option) => {
      option.classList.remove("active", "color-scheme--4");
    });
    if (input.checked) {
      input.closest(".delivery-option")?.classList.add("active", "color-scheme--4");
    }

    const properties = [{ name: "Delivery-Option", value: input.value }];
    // Only the date-bearing option keeps a date; anything else clears it.
    if (input.value !== dateRequiredCode) properties.push({ name: "_delivery-date", value: "" });
    setCartProperties(properties);

    const needsDate = input.value === dateRequiredCode;
    toggleCheckoutButton(!needsDate);
    toggleDateStepIndicator(needsDate);
  };

  // The only clickable part of an option box was the small label that draws the
  // radio, so the rest of the box — title, description, price — was dead space.
  // Delegated on document because the cart drawer replaces this markup on every
  // cart change; clicks that already reach the input or its label are left to
  // the browser, so the option is never recorded twice.
  document.addEventListener("click", (event) => {
    const box = event.target.closest(".delivery-option");
    if (!box || event.target.closest("label, input, a, button")) return;

    box.querySelector('input[type="radio"]')?.click();
  });

  /* ---------------------------------------------------------------------------
     Calendar
     ------------------------------------------------------------------------ */

  const zeroPad = (value) => (Number(value) < 10 ? `0${value}` : String(value));

  const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const addADay = (container, entry) => {
    const wrapper = document.createElement("span");
    wrapper.classList.add("date-wrapper");
    wrapper.setAttribute("data-date", entry.date.toISOString());

    const inner = document.createElement("span");
    inner.classList.add("date-wrapper-inner");
    inner.appendChild(document.createTextNode(entry.date.getDate()));

    const price = document.createElement("span");
    price.classList.add("price-node");
    price.appendChild(document.createTextNode(`£${(entry.price / 100).toFixed(0)}`));
    inner.appendChild(price);

    if (entry.enabled) {
      wrapper.classList.add("enabled-date");
      if (entry.date.getDay() === 6) wrapper.classList.add("saturday-date");
    } else if (entry.placeholder) {
      wrapper.style.opacity = "0.1";
    }

    wrapper.appendChild(inner);
    container.appendChild(wrapper);
  };

  const addMonth = (container, dates, start) => {
    const first = dates[start].date;
    let check = new Date(first);

    // Back to the 1st of the month, then back to the preceding Sunday.
    while (check.getDate() > 1) check = new Date(check.getTime() - ONE_DAY_IN_MS);
    while (check.getDay() > 0) check = new Date(check.getTime() - ONE_DAY_IN_MS);

    // Pad the days before the first available one.
    while (!isSameDay(check, first)) {
      addADay(container, { enabled: false, date: new Date(check), placeholder: true });
      check = new Date(check.getTime() + ONE_DAY_IN_MS);
    }

    let month;
    let i = start;
    for (; i < dates.length; i += 1) {
      const entry = dates[i];
      if (month !== undefined && entry.date.getMonth() !== month) break;

      addADay(container, entry);

      if (!entry.placeholder && month === undefined) {
        month = entry.date.getMonth();
        const monthStr = `${entry.date.toLocaleString("en-GB", { month: "long" })} ${entry.date.getFullYear()}`;
        container.setAttribute("data-month", monthStr);
      }
      check = entry.date;
    }

    // Pad out to the end of the month.
    check = new Date(check.getTime() + ONE_DAY_IN_MS);
    while (check.getMonth() === month) {
      addADay(container, { enabled: false, date: new Date(check), placeholder: true });
      check = new Date(check.getTime() + ONE_DAY_IN_MS);
    }

    return i;
  };

  const setNextPrevState = (currentMonth) => {
    document.querySelector(".date-selector-previous")?.classList.toggle("invisible", !currentMonth.previousSibling);
    document.querySelector(".date-selector-next")?.classList.toggle("invisible", !currentMonth.nextSibling);
  };

  const visibleMonth = () => document.querySelector(".date-selector-month-view:not(.hidden)");

  const setMonthLabel = (monthView) => {
    const label = document.querySelector(".date-selector-month-controls .date-selector-month");
    if (label) label.textContent = monthView.getAttribute("data-month");
  };

  const showNextMonth = () => {
    const current = visibleMonth();
    let next = current.nextSibling;
    if (next && !next.classList.contains("date-selector-month-view")) next = next.nextSibling;
    if (!next) return;

    current.classList.add("hidden");
    next.classList.remove("hidden");
    setMonthLabel(next);
    next.querySelectorAll(".date-wrapper.hidden").forEach((date) => date.classList.remove("hidden"));
    document.querySelector(".date-selector-next-month-controls")?.classList.add("hidden");
    setNextPrevState(next);
  };

  const showPreviousMonth = () => {
    const current = visibleMonth();
    let previous = current.previousSibling;
    if (previous && !previous.classList.contains("date-selector-month-view")) {
      previous = previous.previousSibling;
    }
    if (!previous) return;

    current.classList.add("hidden");
    previous.classList.remove("hidden");
    setMonthLabel(previous);
    setNextPrevState(previous);
  };

  const daysInMonth = (month, year) => new Date(year, month, 0).getDate();

  /**
   * If the first month has no selectable dates, skip to the next one. Otherwise,
   * when the earliest available date leaves less than four weeks in the month,
   * hide the leading blanks and reveal enough of the following month to fill it.
   */
  const trimFirstMonth = (monthView) => {
    if (!monthView.querySelector(".enabled-date")) {
      document.querySelector(".date-selector-next")?.click();
      return;
    }

    const active = visibleMonth();
    if (!active) return;

    const dates = active.querySelectorAll(".date-wrapper");
    const monthYearString = active.getAttribute("data-month");
    if (!monthYearString) return;

    const monthInt = MONTH_STRING_ARRAY.indexOf(monthYearString.split(" ")[0]) + 1;
    const yearInt = Number(monthYearString.split(" ")[1]);
    const totalDays = daysInMonth(monthInt, yearInt);

    for (let i = 0; i < dates.length; i += 1) {
      if (!dates[i].classList.contains("enabled-date")) continue;

      const earliest = new Date(dates[i].dataset.date).getDate();
      const weeksRemaining = (totalDays - earliest) / 7;
      if (weeksRemaining >= 4) break;

      const daysStillToShow = (4 - weeksRemaining) * 7;

      // Trim back to the start of the week containing the first available date.
      let weekStart = 0;
      for (let j = 0; j < dates.length; j += 1) {
        if (!dates[j].classList.contains("enabled-date")) continue;
        weekStart = j;
        while (!Number.isInteger(weekStart / 7)) weekStart -= 1;
        break;
      }
      for (let j = 0; j < weekStart; j += 1) {
        if (!dates[j].classList.contains("enabled-date")) dates[j].classList.add("hidden");
      }

      // Reveal the start of the following month beneath it.
      const source = new Date(dates[i].dataset.date);
      const nextMonthDate = new Date(source.setDate(1));
      nextMonthDate.setMonth(new Date(dates[i].dataset.date).getMonth() + 1);
      const nextMonthLabel = `${MONTH_STRING_ARRAY[nextMonthDate.getMonth()]} ${nextMonthDate.getFullYear()}`;
      const nextMonthContainer = document.querySelector(`.date-selector-month-view[data-month="${nextMonthLabel}"]`);
      if (!nextMonthContainer) break;

      const template = document.querySelector(".date-selector-next-month-controls-template");
      const heading = template.cloneNode(true);
      heading.classList.remove("date-selector-next-month-controls-template", "hidden");
      heading.classList.add("date-selector-next-month-controls");
      heading.querySelector(".date-selector-month").textContent = nextMonthLabel;

      const nextMonthDates = nextMonthContainer.querySelectorAll(".date-wrapper");
      let started = false;
      let shown = 0;
      nextMonthDates.forEach((date) => {
        if (shown < daysStillToShow) {
          if (date.classList.contains("enabled-date") && !started) started = true;
          else if (started) shown += 1;
        } else {
          date.classList.add("hidden");
        }
      });

      document.querySelector(".date-selector-week").insertBefore(heading, visibleMonth().nextSibling);
      nextMonthContainer.classList.remove("hidden");
      break;
    }
  };

  const showAvailableDates = (dates) => {
    const container = document.querySelector(".date-selector-week");
    const monthLabel = document.querySelector(".date-selector-month-controls .date-selector-month");
    if (!container) return;

    container.replaceChildren();

    let i = 0;
    do {
      const monthContainer = document.createElement("div");
      const next = addMonth(monthContainer, dates, i);
      if (i === 0) {
        if (monthLabel) monthLabel.textContent = monthContainer.getAttribute("data-month");
      } else {
        monthContainer.classList.add("hidden");
      }
      monthContainer.classList.add("date-selector-month-view");
      container.appendChild(monthContainer);
      i = next;
    } while (i < dates.length);

    setNextPrevState(container.firstChild);

    calendarListeners?.abort();
    calendarListeners = new AbortController();
    const { signal } = calendarListeners;

    document.querySelector(".date-selector-previous")?.addEventListener("click", showPreviousMonth, { signal });
    document.querySelector(".date-selector-next")?.addEventListener("click", showNextMonth, { signal });

    container.addEventListener(
      "click",
      (event) => {
        const date = event.target.closest(".enabled-date");
        if (!date) return;

        container.querySelectorAll(".selected-date").forEach((el) => el.classList.remove("selected-date"));
        date.classList.add("selected-date");

        const value = new Date(date.getAttribute("data-date"));
        const dateStr = `${value.getFullYear()}-${zeroPad(value.getMonth() + 1)}-${zeroPad(value.getDate())}`;

        const hidden = document.querySelector('input[name="attributes[_delivery-date]"]');
        if (hidden) hidden.value = dateStr;

        nextButton()?.classList.add("hidden");
        checkoutButton()?.classList.remove("hidden");

        setCartProperties([{ name: "_delivery-date", value: dateStr }]);
      },
      { signal },
    );

    trimFirstMonth(container.firstChild);

    // Warn when the basket contains items with differing pre-order dates.
    const items = config()?.basket.items || [];
    const restockDates = new Set(items.map((item) => item.restock));
    document.getElementById("delivery-picker-split-basket")?.classList.toggle("hidden", restockDates.size <= 1);
  };

  const getAvailableDates = (callback) => {
    const settings = config();
    const input = postcodeInput();
    if (!settings || !input) return;

    sendApiRequest("/api/shipping", "POST", { postcode: input.value, items: settings.basket.items }, (err, result) => {
      if (result && result.days && result.days.length > 0) {
        document.getElementById("delivery-picker-date-controls")?.classList.remove("hidden");
        document.getElementById("delivery-picker-no-delivery")?.classList.add("hidden");

        showAvailableDates(
          result.days.map((day) => ({
            date: new Date(day.date),
            enabled: day.enabled,
            price: day.price,
          })),
        );
      } else {
        document.getElementById("delivery-picker-date-controls")?.classList.add("hidden");
        document.getElementById("delivery-picker-no-delivery")?.classList.remove("hidden");
        if (settings.noDeliveryUrl) window.location.href = settings.noDeliveryUrl;
      }

      if (callback) callback();
    });
  };

  /* ---------------------------------------------------------------------------
     Checkout
     ------------------------------------------------------------------------ */

  /**
   * A basket that has already been to checkout has cached carrier rates, so if
   * the date or option changed the service needs to resync before we go back.
   */
  const bindCheckoutSync = () => {
    const button = checkoutButton();
    if (!button || checkoutSyncBound) return;
    checkoutSyncBound = true;

    button.addEventListener("click", (event) => {
      if (typeof hj === "function") hj("event", "reached_checkout");

      const settings = config();
      if (!settings || !settings.basket.checkedOut) return;

      const selectedDate = document.querySelector('input[name="attributes[_delivery-date]"]')?.value;
      const selectedOption = document.querySelector('input[name="attributes[Delivery-Option]"]:checked')?.value;
      const needsDate = selectedOption === settings.dateRequiredCode;

      if (!needsDate) {
        event.preventDefault();
        setCartProperties([{ name: "_delivery-date", value: "" }]).then(() => {
          sendApiRequest("/api/carrier-sync", "POST", { reset: true }, () => {
            window.location.href = "/checkout?step=contact_information";
          });
        });
      } else if (selectedDate !== settings.basket.deliveryDate) {
        event.preventDefault();
        // A plain advance would land on the shipping step and re-serve the
        // rates cached before the date changed.
        sendApiRequest("/api/carrier-sync", "POST", { reset: true }, () => {
          window.location.href = "/checkout?step=contact_information";
        });
      }
    });
  };

  /* ---------------------------------------------------------------------------
     Init
     ------------------------------------------------------------------------ */

  const bindPostcodeInput = () => {
    const input = postcodeInput();
    if (!input || input.dataset.deliveryPickerBound) return;
    input.dataset.deliveryPickerBound = "true";

    input.addEventListener("keydown", (event) => {
      // Enter here would submit the cart form and skip the rest of the flow.
      if (event.key !== "Enter") return;
      event.preventDefault();
      event.stopPropagation();
    });
  };

  const init = () => {
    if (!config()) return;
    bindPostcodeInput();
    checkoutSyncBound = false;
    bindCheckoutSync();
  };

  const onReady = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  };

  onReady(init);

  // The cart drawer replaces its own markup on every cart change, which throws
  // away the picker's DOM and listeners, so rebind afterwards.
  ["on:cart:add", "on:line-item:change", "on:cart-drawer:after-open"].forEach((event) => {
    document.addEventListener(event, () => setTimeout(init, 0));
  });
})();
